import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  BRAND_KEYS,
  CHART_STRATEGIES,
  Chip,
  DEFAULT_BRAND,
  TextField,
  chartCompanionPick,
  normalizeHex,
} from "@outpave/ds";
import type { ChartStrategy } from "@outpave/ds";
import type { ColorScheme } from "context/ColorSchemeContext";
import { ThemeColors } from "utils/themeContextHelper";
import {
  DEFAULT_CHART_STRATEGY,
  chartThirdIndex,
  resolveBrandIdentity,
} from "theme/brandIdentity";
import { Stack, Inline } from "components/layout/reusableLayouts";

/**
 * The branding form, shaped to the package's identity model rather than to the
 * legacy palette. Every knob here is one the package takes; nothing on this
 * form is a derived value, because the package derives those itself and
 * guarantees the contrast pairs while doing it.
 */

export type BrandColorKey =
  | "primary"
  | "secondary"
  | "tertiary"
  | "navBackground"
  | "success"
  | "warning"
  | "danger"
  | "info";

interface Knob {
  key: BrandColorKey;
  label: string;
  description: string;
}

const base = BRAND_KEYS[DEFAULT_BRAND];

// `navBackground` is the record's name for the canvas and stays that way: the
// package calls the same knob `background`, and renaming it here would be a
// data migration for no gain.
const BRAND_KNOBS: Knob[] = [
  { key: "primary", label: "Primary", description: "PrimaryColorDescription" },
  {
    key: "secondary",
    label: "Secondary",
    description: "SecondaryColorDescription",
  },
  {
    key: "tertiary",
    label: "Tertiary",
    description: "TertiaryColorDescription",
  },
  {
    key: "navBackground",
    label: "Background",
    description: "BackgroundColorDescription",
  },
];

const STATUS_KNOBS: Knob[] = [
  { key: "success", label: "Success", description: "SuccessColorDescription" },
  { key: "warning", label: "Warning", description: "WarningColorDescription" },
  { key: "danger", label: "Danger", description: "DangerColorDescription" },
  { key: "info", label: "Info", description: "InfoColorDescription" },
];

const ALL_KNOBS: Knob[] = [...BRAND_KNOBS, ...STATUS_KNOBS];

export const BRAND_COLOR_KEYS: BrandColorKey[] = ALL_KNOBS.map(
  (knob) => knob.key,
);

/** So a surface can name a knob without owning a second copy of the labels. */
export const BRAND_KNOB_LABELS = Object.fromEntries(
  ALL_KNOBS.map((knob) => [knob.key, knob.label]),
) as Record<BrandColorKey, string>;

export const CHART_STRATEGY_KEY = "chartStrategy";
export const CHART_LEAD_KEY = "chartLeadWithSecondary";
export const CHART_THIRD_KEY = "chartThirdColor";
export const BRAND_CHART_KEYS = [
  CHART_STRATEGY_KEY,
  CHART_LEAD_KEY,
  CHART_THIRD_KEY,
];

/** Saved alongside the identity knobs, but rendered on the surface that owns
 *  the card rather than in the colour tabs. */
export const BRAND_CARD_KEYS = [
  "paymentCardBackground",
  "paymentCardTextMode",
  "paymentCardArt",
  "paymentCardFace",
];

/** The chart knobs as the page shows them: stored, else inherited, else the default. */
export interface ChartChoice {
  strategy: ChartStrategy;
  leadWithSecondary: boolean;
  /** 0 is the brand tertiary, 1 to 6 a generated companion. */
  third: number;
}

/** What the chart knobs resolve to, layered per knob the way `resolveBrandIdentity` layers them. */
export const chartChoiceOf = (
  colors: Partial<ThemeColors>,
  inherited?: Partial<ThemeColors> | null,
): ChartChoice => {
  const pick = (key: keyof ThemeColors) =>
    colors[key] || inherited?.[key] || undefined;
  const stored = pick(CHART_STRATEGY_KEY) as ChartStrategy;
  return {
    strategy: CHART_STRATEGIES.includes(stored)
      ? stored
      : DEFAULT_CHART_STRATEGY,
    leadWithSecondary: pick(CHART_LEAD_KEY) === "true",
    third: chartThirdIndex(pick(CHART_THIRD_KEY)),
  };
};

/** Only the knob a pick changed, so the others keep following the layer beneath. */
export const chartKeysOf = (
  next: ChartChoice,
  current: ChartChoice,
): Record<string, string> => ({
  ...(next.strategy !== current.strategy
    ? { [CHART_STRATEGY_KEY]: next.strategy }
    : {}),
  ...(next.leadWithSecondary !== current.leadWithSecondary
    ? { [CHART_LEAD_KEY]: String(next.leadWithSecondary) }
    : {}),
  ...(next.third !== current.third
    ? { [CHART_THIRD_KEY]: String(next.third) }
    : {}),
});

/**
 * What a knob resolves to when this surface has not set it. A knob is set on
 * the branding record or it is not: there is no "app default" tier. Company
 * branding layers over the platform's, so an unset company knob shows what it
 * INHERITS, not the DS seed: a company on a yellow platform that resets its
 * background gets yellow, and telling it "Default: navy" is a lie about what
 * saving does.
 */
export const brandKnobFallback = (
  key: BrandColorKey,
  inherited?: Partial<Record<BrandColorKey, string>>,
): { color: string; source: "inherited" | "default" } => {
  const from = inherited?.[key];
  if (from) return { color: from, source: "inherited" };
  return {
    color: key === "navBackground" ? base.neutral : base[key],
    source: "default",
  };
};

/** Which knob group to render. The tab strip lives on the page (OUT-1379), so
 *  a surface can add tabs of its own without this component knowing. */
export type BrandColorsTab = "brand" | "status" | "chart";

/** Six hex digits, with or without the `#`, or the three-digit shorthand. */
export const parseHexInput = (raw: string): string | null => {
  const text = raw.trim().replace(/^#/, "");
  if (/^[0-9a-f]{6}$/i.test(text)) return `#${text.toLowerCase()}`;
  if (/^[0-9a-f]{3}$/i.test(text)) {
    return `#${[...text.toLowerCase()].map((c) => c + c).join("")}`;
  }
  return null;
};

const bareHex = (hex: string) => normalizeHex(hex);

interface Props {
  tab: BrandColorsTab;
  value: Partial<ThemeColors>;
  /**
   * The layer beneath this one, if any. Company branding passes the platform's
   * record; the platform passes nothing, since it inherits only the DS.
   */
  inherited?: Partial<ThemeColors> | null;
  /** Names the layer in the default tag, e.g. "Platform". */
  inheritedLabel?: string;
  onChange: (key: BrandColorKey, hex: string) => void;
  chart: ChartChoice;
  onChartChange: (next: ChartChoice) => void;
  /** The preview's scheme: generated companions differ between light and dark. */
  scheme: ColorScheme;
  /** The draft's resolved tokens for that scheme, so the palette shows what charts paint. */
  tokens: Record<string, string>;
}

const SectionHeading: React.FC<{ title: string; helper: string }> = ({
  title,
  helper,
}) => (
  <Stack gap="var(--space-1)">
    <Typography variant="h6">{title}</Typography>
    <Typography variant="body2" sx={{ color: "var(--color-text-secondary)" }}>
      {helper}
    </Typography>
  </Stack>
);

const ColorRow: React.FC<{
  knob: Knob;
  hex: string;
  inherited?: Partial<Record<BrandColorKey, string>>;
  inheritedLabel?: string;
  onChange: (hex: string) => void;
}> = ({ knob, hex, inherited, inheritedLabel, onChange }) => {
  const { t } = useTranslation();
  const { color: fallback, source } = brandKnobFallback(knob.key, inherited);
  const shown = hex || fallback;
  const sourceLabel =
    source === "inherited" && inheritedLabel ? inheritedLabel : t("Default");

  // Follows the value when it changes from outside: the picker, a preset, a reset.
  const [draft, setDraft] = useState(bareHex(shown));
  const [synced, setSynced] = useState(shown);
  if (shown !== synced) {
    setSynced(shown);
    setDraft(bareHex(shown));
  }

  // Rejects anything that is not hex outright, rather than filtering "rgb(0, 0, 0)" into one.
  const handleType = (raw: string) => {
    const text = raw.trim().replace(/^#/, "");
    if (/^[0-9a-f]{0,6}$/i.test(text)) setDraft(text.toUpperCase());
  };

  const commit = () => {
    const next = parseHexInput(draft);
    if (next && bareHex(next) !== bareHex(shown)) onChange(next);
    else setDraft(bareHex(shown));
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "var(--space-3)",
        minWidth: 0,
        padding: "var(--space-3)",
        borderRadius: "var(--radius-lg)",
        border: "var(--border-width-1) solid var(--color-border)",
        "&:hover": { backgroundColor: "var(--color-bg-elevated)" },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: 36,
          height: 36,
          flexShrink: 0,
          overflow: "hidden",
          borderRadius: "var(--radius-md)",
          border: "var(--border-width-1) solid var(--color-border)",
          backgroundColor: shown,
          "&:focus-within": {
            borderColor: "var(--color-border-focus)",
            boxShadow: "var(--shadow-focus-brand)",
          },
        }}
      >
        {/* The input IS the swatch: transparent, but focusable and clickable
            across the whole tile, so this needs no separate control. */}
        <input
          id={`branding-color-${knob.key}`}
          type="color"
          aria-label={t("EditColor", { label: t(knob.label) })}
          value={shown}
          onChange={(e) => onChange(e.target.value)}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            opacity: 0,
            cursor: "pointer",
            border: "none",
            padding: 0,
          }}
        />
      </Box>

      <Stack gap="2px" sx={{ flex: 1, minWidth: 0 }}>
        <Inline gap="var(--space-2)" wrap={false}>
          <Typography variant="subtitle2">{t(knob.label)}</Typography>
          {!hex && (
            <Chip size="sm" tone="neutral">
              {sourceLabel}
            </Chip>
          )}
        </Inline>
        <Typography
          variant="caption"
          sx={{ color: "var(--color-text-secondary)" }}
        >
          {t(knob.description)}
        </Typography>
        {/* Any stored value pins the colour, even one equal to what it inherits. */}
        {hex && (
          <Inline gap="var(--space-2)" wrap={false}>
            <Typography
              variant="caption"
              sx={{
                color: "var(--color-text-tertiary)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {t("DefaultHex", {
                label: sourceLabel,
                hex: `#${bareHex(fallback)}`,
              })}
            </Typography>
            <Link
              component="button"
              type="button"
              variant="caption"
              onClick={() => onChange("")}
              aria-label={t("ResetColor", { label: t(knob.label) })}
            >
              {t("Reset")}
            </Link>
          </Inline>
        )}
      </Stack>

      <Box sx={{ width: 120, flexShrink: 0 }}>
        <TextField
          size="md"
          fullWidth
          leading={
            <Typography
              component="span"
              sx={{ color: "var(--color-text-tertiary)" }}
            >
              #
            </Typography>
          }
          value={draft}
          onChange={(e) => handleType(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            if (e.key === "Escape") setDraft(bareHex(shown));
          }}
          inputProps={{
            "aria-label": t("HexValue", { label: t(knob.label) }),
            spellCheck: false,
            style: { fontVariantNumeric: "tabular-nums" },
          }}
        />
      </Box>
    </Box>
  );
};

/** The seven 3rd-slot options, in the package's own numbering: the tertiary, then the companions. */
export const thirdChartOptions = (
  identity: { primary: string; secondary: string; tertiary: string },
  chart: ChartChoice,
  scheme: ColorScheme,
): string[] => {
  const lead = chart.leadWithSecondary ? identity.secondary : identity.primary;
  const other = chart.leadWithSecondary ? identity.primary : identity.secondary;
  return chartCompanionPick(
    lead,
    other,
    identity.tertiary,
    chart.strategy,
    scheme,
    0,
  ).all.map((hex) => `#${hex}`);
};

const Field: React.FC<{
  label: string;
  helper?: string;
  children: React.ReactNode;
}> = ({ label, helper, children }) => (
  <Stack gap="var(--space-2)">
    <Typography variant="subtitle2">{label}</Typography>
    {children}
    {helper && (
      <Typography
        variant="caption"
        sx={{ color: "var(--color-text-tertiary)" }}
      >
        {helper}
      </Typography>
    )}
  </Stack>
);

const Dot: React.FC<{ color: string }> = ({ color }) => (
  <Box
    aria-hidden
    sx={{
      width: 14,
      height: 14,
      flexShrink: 0,
      borderRadius: "var(--radius-pill)",
      border: "var(--border-width-1) solid var(--color-border)",
      backgroundColor: color,
      marginRight: "var(--space-2)",
    }}
  />
);

const ChartSection: React.FC<{
  value: Partial<ThemeColors>;
  inherited?: Partial<ThemeColors> | null;
  chart: ChartChoice;
  onChartChange: (next: ChartChoice) => void;
  scheme: ColorScheme;
  tokens: Record<string, string>;
}> = ({ value, inherited, chart, onChartChange, scheme, tokens }) => {
  const { t } = useTranslation();
  const identity = resolveBrandIdentity(value, inherited) ?? base;
  const options = thirdChartOptions(identity, chart, scheme);

  return (
    <Stack gap="var(--space-5)">
      <SectionHeading title={t("Chart colors")} helper={t("ChartColorsHelp")} />

      <Field label={t("Charts lead with")}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={chart.leadWithSecondary ? "secondary" : "primary"}
          onChange={(_, next) =>
            next &&
            onChartChange({ ...chart, leadWithSecondary: next === "secondary" })
          }
          aria-label={t("Charts lead with")}
        >
          <ToggleButton value="primary">
            <Dot color={identity.primary} />
            {t("Primary")}
          </ToggleButton>
          <ToggleButton value="secondary">
            <Dot color={identity.secondary} />
            {t("Secondary")}
          </ToggleButton>
        </ToggleButtonGroup>
      </Field>

      <Field
        label={t("Companion strategy")}
        helper={t(`ChartStrategy_${chart.strategy}_help`)}
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          value={chart.strategy}
          onChange={(_, next: ChartStrategy | null) =>
            next && onChartChange({ ...chart, strategy: next })
          }
          aria-label={t("Companion strategy")}
          sx={{ flexWrap: "wrap" }}
        >
          {CHART_STRATEGIES.map((strategy) => (
            <ToggleButton key={strategy} value={strategy}>
              {t(`ChartStrategy_${strategy}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Field>

      <Field label={t("3rd chart color")} helper={t("ChartThirdColorHelp")}>
        <Inline gap="6px" wrap={false}>
          {options.map((hex, index) => {
            const selected = index === chart.third;
            return (
              <Box
                key={index}
                component="button"
                type="button"
                aria-pressed={selected}
                aria-label={
                  index === 0
                    ? t("ChartThirdTertiary")
                    : t("ChartThirdGenerated", { n: index })
                }
                onClick={() => onChartChange({ ...chart, third: index })}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: 32,
                  padding: 0,
                  cursor: "pointer",
                  borderRadius: "var(--radius-md)",
                  border: "2px solid",
                  borderColor: selected
                    ? "var(--color-border-focus)"
                    : "transparent",
                  backgroundColor: hex,
                  "&:hover": {
                    borderColor: selected
                      ? "var(--color-border-focus)"
                      : "var(--color-border)",
                  },
                  "&:focus-visible": {
                    outline: "none",
                    boxShadow: "var(--shadow-focus-brand)",
                  },
                }}
              />
            );
          })}
        </Inline>
      </Field>

      <Field label={t("Palette")}>
        <Inline gap="6px" wrap={false} data-testid="chart-palette">
          {Array.from({ length: 8 }, (_, i) => (
            <Box
              key={i}
              title={`--color-chart-${i + 1}`}
              data-color={tokens[`color-chart-${i + 1}`]}
              sx={{
                flex: 1,
                minWidth: 0,
                height: 32,
                borderRadius: "var(--radius-md)",
                border: "var(--border-width-1) solid var(--color-border)",
                backgroundColor: tokens[`color-chart-${i + 1}`],
              }}
            />
          ))}
        </Inline>
      </Field>
    </Stack>
  );
};

const BrandColorsPanel: React.FC<Props> = ({
  tab,
  value,
  inherited,
  inheritedLabel,
  onChange,
  chart,
  onChartChange,
  scheme,
  tokens,
}) => {
  const { t } = useTranslation();

  const rows = (knobs: Knob[]) => (
    <Stack gap="var(--space-2)">
      {knobs.map((knob) => (
        <ColorRow
          key={knob.key}
          knob={knob}
          hex={value[knob.key] || ""}
          inherited={inherited ?? undefined}
          inheritedLabel={inheritedLabel}
          onChange={(hex) => onChange(knob.key, hex)}
        />
      ))}
    </Stack>
  );

  if (tab === "chart") {
    return (
      <ChartSection
        value={value}
        inherited={inherited}
        chart={chart}
        onChartChange={onChartChange}
        scheme={scheme}
        tokens={tokens}
      />
    );
  }

  return (
    <Stack gap="var(--space-4)">
      {tab === "brand" ? (
        <SectionHeading
          title={t("Brand colors")}
          helper={t("BrandColorsHelp")}
        />
      ) : (
        <SectionHeading
          title={t("Status colors")}
          helper={t("StatusColorsHelp")}
        />
      )}
      {rows(tab === "brand" ? BRAND_KNOBS : STATUS_KNOBS)}
    </Stack>
  );
};

export default BrandColorsPanel;
